import React from 'react';
import BaseView from '../../components/CRUDView';
import {TractorOwnerEndpoints, TractorsEndpoints} from "../../common/ProxyEndpoints";
import Utils from "../../common/Utils";
import Avatar from '../../components/ProfileAvatar';
import EntityImageDisplay from "../../components/EntityImageDisplay/EntityImageDisplay";
import TractorUtils from "./TractorUtils";
import {Permissions, TractorState} from "../../common/Constants";
import DialogCollapseSection from "../../components/DialogCollapseSection/DialogCollapseSection";
import HistoryLink from "../../common/HistoryLink";
import {Badge} from 'reactstrap';
import OperationSummaryComponent, {FixedValueDisplay} from "../../components/OperationSummaryComponent/OperationSummaryComponent";
import AppVariables from "../../common/AppVariables";
import {
    ConditionMap,
    CustomTractorsFilterView,
    parseTractorQueryFilters,
    TRACTORS_QUERY_FILTERS
} from "./TractorsCommon";
import CreateTractorDialog from "../../components/CreateTractorDialog/CreateTractorDialog";
import {ExpanderView, SectionedList} from "../../components/AuditExplorer/Elements";
import AssignmentRulesConfigDialog, {AssignmentRuleInfo} from "./AssignmentRulesConfigDialog";
import NumberLabel from "../../components/NumberLabel/NumberLabel";
import WorkloadComponent from "./WorkloadComponent";
import {broadcastEvent} from "../../common/Events/EventSource";
import EventActions from "../../common/Events/Actions";
import {getActiveQuantityInfo, getQuantityUnit, getQuantityValue} from "../../common/QuantityHelper";

const SearchProps = [
    'name',
    'chassis',
    'tractor_model',
    'registration_number',
    'year',
    'operator.name'
];

const CacheDependency = [
    'dashboard',
    'operators'
];

/***
 * Handles management of tractors for a tractor owner
 */
class TractorsOwner extends BaseView {

    constructor(props){
        super(props,'tractors_owner');
    }

    getOwnerId(){
        return this.getProxy().getUserInfo().owner.id;
    }

    fetchOperatorsForOwner(ownerId,idleOnly,exclude,include){

        //  already loaded
        if(ownerId == this.state.sourceOwner){
            return;
        }

        const me = this;

        this.setState({
            sourceOwner: ownerId,
            isBusy: true
        });

        return TractorUtils.fetchOwnerOperators(ownerId,idleOnly,exclude,include).then(operators =>{

            me.setState({
                operators: operators
            });

        }).catch(err =>{

            //  show error and allow retry option


        }).then(() =>{

            me.setState({
                isBusy: false
            });

        });

    }

    getMyOperators(updateState = false,busyState = false){

        if(busyState){
            this.setStateSafe({
                isBusy: true
            });
        }

        return this.proxy.processRequest(TractorOwnerEndpoints.getOperators(this.getOwnerId()).url,{
            fields: [ 'id' , 'name'  ],
            include: [{
                model: 'tractor',
                attributes: ['id']
            }],
            method: 'get'
        }).then(response => {

            const payload = response.getPayload();

            if(updateState)
                this.setStateSafe({
                    operators: payload
                });

            if(busyState){
                this.setStateSafe({
                    isBusy: false
                });
            }

            return payload;
        }).catch(err =>{

            if(busyState){
                this.setStateSafe({
                    isBusy: false
                });
            }

            return Promise.reject(err);
        });

    }

    fetchRequests(tractor,range,customRange){

        const me = this;

        this.setState({
            isBusy: true
        });

        //  get requests for operator
        return this.proxy.processRequest(TractorsEndpoints.getOperationSummary(tractor.id,range,customRange)).then(response => {

            tractor.operationSummary = response.getPayload();

            me.setState({
                summaryError: null,
                detailItem: tractor,
                isBusy: false
            });

        }).catch(err => {

            me.setState({
                isBusy: false,
                summaryError: err
            });

        });
    }

    beginShowDetails(tractor){

        //  fetch requests assigned to tractor
        this.fetchRequests(tractor);

        //  relay to base view
        super.beginShowDetails(tractor);
    }

    getCustomFilterState(filters) {
        return parseTractorQueryFilters(filters);
    }

    beginShowCreate() {
        this._createTractorDlg.show({ owner: this.proxy.getUserInfo().owner });
    }

    renderDialogs(dialog) {

        return <React.Fragment>
            {super.renderDialogs(dialog)}
            <CreateTractorDialog ref={r => this._createTractorDlg = r}  includeOwner={false} onExit={tractor => this.refresh()}/>
        </React.Fragment>;

    }

    componentWillMount(){

        const me = this;

        this.setViewOptions({

            //  The message to show when there are no items in the view
            noRowsMessage: 'No tractors available yet!',

            //  The class icon for the card header
            pageIcon: 'fa fa-truck',

            //  The title for the content frame
            pageTitle: 'Tractors',

            //  The total number of items per page
            maxItemsPerPage: 10,

            //  Defines the features of the view
            features: [ 'toolbox' , 'search' ],

            multiSelection: {

                //  enable multi-selection
                enable : false,
            },

            model:{
                include: [ 'operator' , 'owner' , 'media' , 'assignment_rules' ]
            },

            hooks:{

                //  Called before every query to get items
                preQueryHook: () => {

                    return me.getMyOperators().then(operators => {

                        return {
                            operators: operators
                        };

                    });

                },

                postProcessorHook: (action,response) => {
                    Utils.eraseSession(CacheDependency);
                },

                renderCellActions: (item) => [
                    {
                        //  Show tractor requests
                        label: "View Requests" , icon: 'fa fa-eye' , onClick: () => {
                            return Utils.navigateLink(`/requests?tractor_id=${encodeURIComponent(item.id.toString())}&_tractor_select_name=${encodeURIComponent(item.name)}`)
                        }
                    }
                ]

            },

            //  defines (CRUD) actions enabled on the items
            actions:{

                //  create action options
                create:{
                    text: 'Add Tractor',
                    successCreateMsg: 'Tractor added successfully!',
                    failCreateMsg : 'Failed adding tractor!',
                    enabled: true,
                    editTitleMsg: 'Edit Tractor',
                    createTitleMsg: 'Add Tractor',
                    permissions:  [ Permissions.Tractors.Create ]

                },

                //  delete action options
                delete:{

                    enabled : false,

                    //  Disallows the deleting of many items ??
                    disallowMany: false,

                    successDeleteMsg: 'Deleted tractor successfully!',
                    failDeleteMsg: 'Failed deleting tractor!',
                    text: 'Delete Tractor',
                    confirmTitle: 'Confirm Delete Tractor',
                    confirmMessage: 'Are you sure you want to delete tractor?',

                    //  The message to show when deleting many items
                    confirmManyMessage: 'Delete selected tractors?'
                },

                //  delete action options
                edit:{
                    enabled : true,
                    successEditMsg: 'Tractor edited successfully!',
                    failEditMsg: 'Failed editing tractor',
                    text: 'Edit Tractor',
                    permissions:  [ Permissions.Tractors.Update ]
                }

            },

            queryActions: {
                endpoint: TractorOwnerEndpoints.getTractors(this.getOwnerId()).url,
                endpointSingle: TractorsEndpoints.getUrl()
            },


            filters: Utils.excludeFilters(TRACTORS_QUERY_FILTERS,['owner.id','tractorOwnerName']),

            renderCustomFilters: (props) => <CustomTractorsFilterView includeOwner {...props}/>,

            search:{

                //
                placeholder: 'Search tractors here...',

                //  the mode for searching
                //  immediate: responsive
                //  explicit: wait till user hits the search button
                mode: 'explicit',

                //  The minimum characters to begin search with
                minSearchChars: 2,

                properties: SearchProps
            },

            sort: {

                properties: [
                    {
                        label: "Date Added",
                        value: "date_created"
                    },
                    {
                        label: "Name",
                        value: "name"
                    },
                    {
                        label: "Registration Number",
                        value: "registration_number"
                    }
                ]
            },

            //  Defines actions for toolbox
            toolBox: [
                {  text : 'Add Tractor'  , icon : 'fa fa-plus'  , invoke: () => me.beginShowCreate() , position: 'right' , permissions:  [ Permissions.Tractors.Create ] },
                {  text : 'Refresh'  , icon : 'fa fa-refresh'  ,
                    invoke: () => {

                        //  refresh view
                        me.refresh();

                        //  broadcast event for child components
                        broadcastEvent(EventActions.RefreshActivePage);

                    } ,
                    position: 'right'
                },
                {  text : 'Condition'  , icon : 'fa fa-bars' , invoke: (index) => me.updateQuery('state',ConditionMap[index].value) , position: 'right' , group : 'filter' , items: ConditionMap.map(x => x.label) },
                {  text : null  , icon : 'fa fa-sort' , invoke: (index) => { me.configureSort();  }, position: 'right' , group : 'filter' }
            ],

            //  Inputs used in editing and creating items
            inputs: [
                    {name: 'name', title: 'Name' , hint: 'Enter tractor name' , type: 'text' , required : true },
                    {
                        group:[
                                {name: 'tractor_model', title:'Model' , hint: 'Enter tractor model' , type: 'text' , required: true , icon: 'fa fa-truck' },
                                {name: 'registration_number', title:'Registration Number' , type: 'text' , hint: 'Enter registration number here...' , required: true },
                                {name: 'chassis', title: 'Chassis'  , hint: 'Enter tractor chassis number'  ,type: 'text' , required: true },
                                {name: 'year', title:'Year', type: 'select', format: 'number' , setValue: (input,item) => input.value =  item.year, items: () => Utils.GenerateRange(1990,Number(AppVariables.ApplicationYear)).map((item) => { return { value: item , text: item } }) , required: true },
                            ],
                        render: (content,key) => {
                            return <DialogCollapseSection initiallyOpen={true} title={"Description"} key={key}>
                                {content}
                            </DialogCollapseSection>
                        }
                    },
                    { name: 'state' , title: 'Tractor Status' , type: 'select' , format: 'number' , comment: <p>Indicates whether you tractor is available to be assigned to requests</p> , items:
                            [
                                { text : "Active" , value: TractorState.Active },
                                { text : "Inactive (Not Assignable)" , value: TractorState.Faulted },
                            ]
                    },
                    {
                        group: [
                            {   name: 'operator', title: 'Operator' , type: 'select' , format: 'number' , icon: 'fa fa-user',

                                comment: <p>Assign an operator to the tractor by selecting it below. A tractor can be created without assigning any operator</p>,

                                setValue: (input,item) => {
                                    input.value = item.operator ? item.operator.id : '';
                                },

                                refresh: () => {
                                    return me.getMyOperators(true,true );
                                },

                                items: () => {

                                    let isEditing = me.state.editItem != null;
                                    let operators = me.state.operators || [];

                                    if(isEditing){

                                        let values = [];
                                        const cOperator = me.state.editItem.operator;
                                        if(cOperator)
                                            values = [ cOperator ];

                                        if(operators.length > 0)
                                            values = values.concat(operators.filter(x => x.tractor == null &&  (cOperator ? x.id != cOperator.id : true) ));

                                        operators = values.map(item => {
                                            return {value: item.id, text: item.name}
                                        }).concat({ value: '' , text: "None" });

                                    }
                                    else {

                                        if (operators.length > 0) {

                                            operators = Utils.insertNone(operators.filter(x => x.tractor == null).map((item) => {
                                                return {value: item.id, text: item.name}
                                            }));
                                        }

                                    }

                                    return operators.length > 0  ?  operators : [{ value: '' , text : 'None' }];
                                }
                            }
                        ],

                        render: (content,key) => {
                            return (<div key={key}>
                                <hr/>
                                <DialogCollapseSection  title={"Optional"}>
                                    {content}
                                </DialogCollapseSection>
                            </div>)
                        }
                    }
            ],

            // Describes the columns present in the table
            columns : [
                {key: '__img' , label: ' ' , cell: (item,k) => <Avatar media={item.media} name={Utils.formatAvatarName(item.name)} size={32} /> , mobile: true },
                {key: 'name', label: () => me.sortCol('Name','name') , mobile: true },
                {key: 'registration_number', label: () => me.sortCol('Reg. No','registration_number') , mobile: true },
                //{key: 'owner', label: 'Owner' , format: (owner) => owner ? owner.name : null },
                {key: 'operator', label: 'Operator' , format: (operator) => operator ? operator.name : <h6><Badge pill={true}>Unassigned</Badge></h6> },
                {key: 'workload', label: 'Workload' , cell: item => <WorkloadComponent tractor={item}/> },
            ],

            display: {
                rowClass: tractor => tractor.state == TractorState.Faulted ? 'inactive-tractor-row' : ''
            },

            //  Describes details to show when clicked
            detailsDisplay: {

                //  enable or disable
                enabled: true,

                renderTitle: () =>{
                    return "Tractor Info";
                },

                renderActions: (item,  { toggle }) => ([
                    {
                        label: "View Requests" , icon: "fa fa-eye", color: "success", onClick: () => {
                            toggle();
                            return Utils.navigateLink(`/requests?tractor_id=${encodeURIComponent(item.id.toString())}&_tractor_select_name=${encodeURIComponent(item.name)}`)
                        }
                    }
                ]),

                customRender: (item) => {

                    const me = this;

                    return <div>
                        <div className="text-center">
                            <h5>Attached Image</h5>
                            <EntityImageDisplay
                                entityName="tractor"
                                defaultSrc={"/img/avatars/tractor_utility.jpg"}
                                entityId={item.id}
                                crudView={me}
                                onCompleteUpload={(media) => {
                                    me.updateItemMedia(item,media);
                                    return false;
                                }}
                                media={item.media} />

                        </div>

                        <div>

                            <DialogCollapseSection title={"Info"} initiallyOpen={true}>
                                <hr/>
                                <div className="row">
                                    <div className="col-md-6">
                                        <dl>
                                            <dt>Name</dt>
                                            <dl>{item.name}</dl>

                                            <dt>Chassis</dt>
                                            <dl>{item.chassis}</dl>

                                            <dt>Model</dt>
                                            <dl>{item.tractor_model}</dl>

                                            <dt>Year</dt>
                                            <dl>{item.year}</dl>

                                        </dl>
                                    </div>
                                    <div className="col-md-6">
                                        <dl>
                                            <dt>Registration No.</dt>
                                            <dl>{item.registration_number}</dl>

                                            <dt>Current State</dt>
                                            <dl>{TractorUtils.renderState(item)}</dl>

                                            <dt>Operator</dt>
                                            <dl>{item.operator ? <HistoryLink url={`/operators?filter_id=${item.operator.id}&q=${item.operator.name}`} className="btn-link" tooltip='Click to see tractor operator'>{item.operator.name}</HistoryLink> :  <h6><Badge pill={true}>Unassigned</Badge></h6>}</dl>

                                            <dt>Date Added</dt>
                                            <dl>{Utils.formatDateStandard(item.date_created)}</dl>

                                        </dl>
                                    </div>
                                </div>
                            </DialogCollapseSection>

                            <OperationSummaryComponent
                                operationSummary={item.operationSummary}
                                onQuery={(timeRange,rangeInfo) => me.fetchRequests(item,timeRange,rangeInfo)}
                                renderRevenueStats={summary => (<div>

                                    <div className="row">

                                        <div className="col-md-4 text-center">
                                            <h6><Badge pill color={"info"}>Pending <i className="fa fa-chevron-up"/></Badge></h6>
                                            <FixedValueDisplay id="total_pending_income"
                                                               value={summary.totalPendingIncomePayout}
                                                               message={AppVariables.CurrencyCode}
                                                               hint="Indicates revenue to be generated from assigned requests"/>

                                        </div>

                                        <div className="col-md-4 text-center">
                                            <h6><Badge pill>Commission <i className="fa fa-chevron-down"/></Badge></h6>

                                            <FixedValueDisplay id="total_commission_income"
                                                               value={summary.totalIncomeCommission}
                                                               message={AppVariables.CurrencyCode}
                                                               hint="Shows total commission revenue deducted from completed requests"/>
                                        </div>

                                        <div className="col-md-4 text-center">

                                            <h6><Badge pill color={"success"}>Completed <i className="fa fa-chevron-down"/></Badge></h6>

                                            <FixedValueDisplay id="total_completed_income"
                                                               value={summary.totalIncomePayout}
                                                               message={AppVariables.CurrencyCode}
                                                               hint="Shows total revenue generated from completed requests"/>

                                        </div>



                                    </div>


                                </div>)}
                                renderOperationStats={summary => (<div>

                                    <div className="row">

                                        <div className="col-md-4 text-center">
                                            <h6><Badge pill color={"danger"}>Cancelled <i className="fa fa-close"/></Badge></h6>
                                            <FixedValueDisplay id="total_cancelled_requests"
                                                               value={summary.totalCancelledRequests}
                                                               hint="Shows to total number of requests cancelled within the given time specified"/>
                                        </div>


                                        <div className="col-md-4 text-center">
                                            <h6><Badge pill color={"info"}>Assigned <i className="fa fa-chevron-up"/></Badge></h6>
                                            <FixedValueDisplay id="total_assigned_acres"
                                                               value={getQuantityValue(summary,{ acre: 'totalAcresAssigned' , hectare: 'totalHectaresAssigned' }) }
                                                               message={getQuantityUnit( getQuantityValue(summary,{ acre: 'totalAcresAssigned' , hectare: 'totalHectaresAssigned' })  ).toLowerCase() }
                                                               hint={`Shows total ${getActiveQuantityInfo().plural.toLowerCase()} assigned to the tractor over the time range specified`}/>
                                        </div>

                                        <div className="col-md-4 text-center">
                                            <h6><Badge pill >Completed <i className="fa fa-chevron-down"/></Badge></h6>
                                            <FixedValueDisplay id="total_completed_acres"
                                                               value={ getQuantityValue(summary,{ acre: 'totalAcresCompleted' , hectare: 'totalHectaresCompleted' })}
                                                               message={getQuantityUnit( getQuantityValue(summary,{ acre: 'totalAcresCompleted' , hectare: 'totalHectaresCompleted' })  ).toLowerCase()}
                                                               hint={`Shows completed ${getActiveQuantityInfo().plural.toLowerCase()} within specified time`}/>
                                        </div>

                                    </div>

                                    <hr/>

                                    <div className="m-4">

                                        <SectionedList items={[
                                            {
                                                title: "Total Assigned Hour(s)",
                                                value: <NumberLabel value={summary.totalAssignedDuration} maximumFractionDigits={1}/>
                                            },
                                            {
                                                title: "Total Working Hour(s)",
                                                value: <NumberLabel value={summary.totalWorkingDuration} maximumFractionDigits={1}/>
                                            }
                                        ]}/>

                                    </div>

                                </div>)}/>

                            <div className="mt-2">

                                <ExpanderView isExpanded={false}
                                              alignContent={false}
                                              title="Assignment Conditions">

                                    <AssignmentRulesConfigDialog ref={r => this._assignmentConfigDlg = r}
                                                                 afterUpdate={async () => {

                                                                     //  query rules for tractor and update model
                                                                     item = await this.updateItemSingle(item.id);

                                                                     // refresh detail item
                                                                     this.setStateSafe({ detailItem: item });

                                                                 }}/>

                                    <AssignmentRuleInfo rules={item.assignment_rules}
                                                        onEdit={() => this._assignmentConfigDlg.show(item)}/>

                                </ExpanderView>
                            </div>

                        </div>

                    </div>

                }
            },

            //  Processors
            processors :{

                //  create new item
                createItem: (proxy , payload) => {

                    if(payload.operator){
                        payload.operator_id = payload.operator;
                    }

                    //  remove unwanted properties
                    delete payload.operator;

                    //  use proxy to create a new tractor for the selected operator
                    return proxy.processRequest(TractorsEndpoints.create(me.getOwnerId(),payload)).then(response => {
                        return this.getMyOperators(true).then(() => response);
                    });
                },

                //  called to delete many items
                deleteMany: (proxy,items) => {
                    return proxy.processRequest(TractorsEndpoints.deleteTractor(items.map((item) => item.id)));
                },

                //  on edit item
                onEditItem: (proxy,item, payload , affected ) => {

                    affected.owner_id = me.getOwnerId();

                    if(affected.operator != (item.operator ? item.operator.id : null)){
                        affected.operator_id = affected.operator;
                    }

                    if(!affected.operator){
                        affected.operator_id = null;
                    }

                    //
                    delete affected.operator;

                    if(Object.keys(affected).find(x => affected[x] !== undefined)) {

                        affected.last_updated = item.last_updated;
                        return proxy.processRequest(TractorsEndpoints.update(item.id, affected)).then(response =>{
                            return this.getMyOperators(true).then(() => response);
                        });

                    }
                    else{
                        return Promise.resolve();
                    }

                },

                //  on delete item
                onDeleteItem: ( proxy,item) => {
                    return proxy.processRequest(TractorsEndpoints.deleteTractor(item.id));
                }

            }
        });

    }

}

export default TractorsOwner;



// WEBPACK FOOTER //
// ./src/views/Tractors/TractorsOwner.js